// BoardScope: what the workspace's overview is narrowed to, said along the
// top of its pane: the range it counts, each clause a click on the board
// narrowed it to (to take out), and the sessions picked into it. Clicks on
// the board narrow it apart from the sidebar's own search
// (lib/sidebarSearch.js): handed to the sidebar from here, as the sidebar's
// is handed to the board from there. How many it reaches the board says
// itself.
//
// The board also lends the sidebar's search what to find among
// (useLendFinder): the kinds of things it finds, the sessions the board read,
// and a step's clause. The last board's stays once it goes.
import { useEffect, useRef, useSyncExternalStore } from "react";
import DateRangePicker from "./DateRangePicker.jsx";
import FoldedChips from "./FoldedChips.jsx";
import { clausesOf } from "./SearchPalette.jsx";
import { useShellControls } from "../lib/shellControls.js";
import { useSidebarSearch } from "../lib/sidebarSearch.js";
import { setWorkspacePicks, useWorkspacePicks, useWorkspaceSearch } from "../lib/workspaceSearch.js";

// Inside the pane's frame, the bar's foot is at a rail button's, so it reads
// as one row with the sidebar's search and the dock's tabs.
export const SCOPE_BAR_H = 37;

const NONE = Object.freeze([]);
const NO_FINDER = Object.freeze({ kinds: NONE, sessions: NONE, clauseFor: null });
let lent = NO_FINDER;
const subscribers = new Set();
const subscribe = (fn) => {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
};
const read = () => lent;

export function useLendFinder({ kinds, sessions, clauseFor }) {
  useEffect(() => {
    lent = { kinds: kinds || NONE, sessions: sessions || NONE, clauseFor: clauseFor || null };
    for (const fn of subscribers) fn();
  }, [kinds, sessions, clauseFor]);
}

export function useBoardFinder() {
  return useSyncExternalStore(subscribe, read, read);
}

const PILL = "inline-flex h-[24px] min-w-0 items-center gap-[5px] overflow-hidden rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] pl-[8px] pr-[2px] text-[12px]";
const PILL_X = "grid h-[20px] w-[18px] shrink-0 place-items-center text-[14px] leading-none text-[color:var(--muted-soft)] hover:text-[color:var(--ink)]";
const MORE = `${PILL} shrink-0 pr-[8px] text-[color:var(--ink)] hover:text-[color:var(--green)]`;
const ACT = "shrink-0 whitespace-nowrap text-[12px] text-[color:var(--muted)] hover:text-[color:var(--ink)]";

function drawPill(x, fit) {
  return (
    <span key={x.key} className={`${PILL} ${fit}`} data-board-scope-pill="">
      {x.negated ? <span className="shrink-0 text-[color:var(--muted)]">not</span> : null}
      {x.field ? <span className="shrink-0 text-[color:var(--muted)]">{x.field}</span> : null}
      <span className="min-w-0 max-w-[200px] truncate text-[color:var(--ink)]">{x.value}</span>
      <button type="button" aria-label={x.dropLabel} onClick={x.drop} className={PILL_X}>&times;</button>
    </span>
  );
}

// `notice`: what the board has to say before what it is narrowed to (the
// workspace the pane showed no longer kept here).
export default function BoardScope({ height = SCOPE_BAR_H, notice = null }) {
  const [text, setText] = useWorkspaceSearch();
  const [own, setOwn] = useSidebarSearch();
  const [picks] = useWorkspacePicks();
  const shell = useShellControls();
  const barRef = useRef(null);
  const { pills, without } = clausesOf(text);
  const narrowed = pills.length > 0 || picks.length > 0;
  const items = pills.map((p, i) => ({ key: `${p.text}:${i}`, field: p.field, negated: p.negated, value: p.value, drop: () => setText(without(i), { now: true }), dropLabel: `Take ${p.value} off the board` }));
  const picked = picks.length ? (
    <span className={`${PILL} shrink-0`} data-board-scope-picks="">
      <span className="min-w-0 truncate text-[color:var(--ink)]">{picks.length === 1 ? "1 session picked" : `${picks.length} sessions picked`}</span>
      <button type="button" aria-label="Take the picked sessions off the board" onClick={() => setWorkspacePicks([])} className={PILL_X}>&times;</button>
    </span>
  ) : null;
  return (
    <div ref={barRef} className="flex min-w-0 items-center gap-[6px] overflow-hidden" style={{ height }} data-board-scope="">
      {shell?.onRangeChange ? (
        <span className="flex shrink-0 items-center self-stretch border-r border-[color:var(--line-soft)] pr-[10px]">
          <DateRangePicker range={shell.range} onRangeChange={shell.onRangeChange} compact />
        </span>
      ) : null}
      {/* The clauses give way before what acts on them. */}
      <span className="flex min-w-0 flex-1 items-center gap-[6px]">
        {notice ? <span className="min-w-0 shrink truncate pl-[4px] text-[12px] text-[color:var(--muted)]" data-board-scope-notice="">{notice}</span> : null}
        {items.length ? (
          <FoldedChips items={items} chip={drawPill} moreClassName={MORE} anchor={barRef} gap={6} after={picked} />
        ) : picked || (notice ? null : <span className="shrink-0 pl-[4px] text-[12px] text-[color:var(--muted)]">Every session</span>)}
      </span>
      {narrowed ? (
        <span className="flex shrink-0 items-center gap-x-[14px] pl-[10px]">
          {pills.length && own.trim() !== text.trim() ? <button type="button" onClick={() => setOwn(text)} className={ACT} data-board-scope-to-sidebar="">Search in the sidebar</button> : null}
          <button type="button" onClick={() => { setText("", { now: true }); setWorkspacePicks([]); }} className={ACT} data-board-scope-clear="">Clear</button>
        </span>
      ) : null}
    </div>
  );
}
